import React from "react";
import { Box, Button, Stack, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReadMoreRoundedIcon from "@mui/icons-material/ReadMoreRounded";
import type { FatigueDerivedMetric } from "../data/types";
import {
  AnimatedCounter,
  GlassCard,
  MetricRing,
  TrendChip,
  tierColor,
} from "./primitives";
import { RiskTierBadge } from "./RiskTierBadge";

interface KpiCardProps {
  metric: FatigueDerivedMetric;
  /** Plain-English "What is X?" summary surfaced via the info tooltip. */
  explanation: string;
  /** Opens the "{CODE} Breakdown" panel. */
  onClick?: () => void;
  decimals?: number;
}

/** Headline KPI card: ring fill, animated value, trend vs baseline, RAG tier. */
export const KpiCard: React.FC<KpiCardProps> = ({
  metric,
  explanation,
  onClick,
  decimals = 0,
}) => {
  const theme = useTheme();
  const accent = tierColor(metric.riskTier, theme);

  return (
    <GlassCard accent={accent} glow={metric.riskTier === "red"}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Stack spacing={0.25}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1.5 }}>
                {metric.code}
              </Typography>
              <Tooltip title={explanation} arrow>
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", fontSize: 16 }}
                  aria-label={`About ${metric.name}`}
                />
              </Tooltip>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {metric.name}
            </Typography>
          </Stack>
          <RiskTierBadge tier={metric.riskTier} size="small" />
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <MetricRing
            value={metric.fill}
            display={`${Math.round(metric.value)}`}
            color={accent}
            size={104}
          />
          <Stack spacing={0.75} sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800} lineHeight={1}>
              <AnimatedCounter value={metric.value} decimals={decimals} />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {metric.unit}
            </Typography>
            <TrendChip
              trend={metric.trend}
              label={metric.trendLabel}
              goodDirection={metric.goodDirection}
            />
            <Typography variant="caption" color="text.secondary">
              {metric.baselineLabel}
            </Typography>
          </Stack>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1.5, fontStyle: "italic" }}
        >
          {metric.question}
        </Typography>

        {onClick && (
          <Button
            onClick={onClick}
            fullWidth
            size="small"
            variant="outlined"
            endIcon={<ReadMoreRoundedIcon />}
            sx={{
              mt: "auto",
              pt: 0.75,
              mb: 0,
              fontWeight: 700,
              color: accent,
              borderColor: alpha(accent, 0.5),
              "&:hover": { borderColor: accent, bgcolor: alpha(accent, 0.08) },
            }}
          >
            {metric.code} Breakdown
          </Button>
        )}
      </Box>
    </GlassCard>
  );
};
